import React, { useState, useEffect } from 'react';
import {
  IconArrowCascadeRight,
  IconArrowDropDown,
  IconCheckCircle,
  Button,
  StatusIndicator,
  STATUS_INDICATOR_VARIANTS,
} from 'cdk-radial';
import './APIPricingPlanTile.scss';
import {
  getPlanName,
  formatNumbers,
  PLAN_TYPE,
  PLAN_CARD_TEXT,
  getPlanCTA,
  formatCurrency,
  getPriceFontSize,
  getLocationFontSize,
} from '../utils/apiPricingUtil';
import { Constants } from '../../../constants/constants';
import { useSelector } from 'react-redux';

const APIPricingPlanTile = (props) => {
  const {
    plan,
    isAsync,
    allEndpoints,
    instanceIdVersionMap,
    selectPricingPlan,
    isSelected,
    isAPIinCart,
    isAPIContracted,
    isAPIPurchased,
    isLoading,
    isRetired,
    statusIndicatorLabel,
    isBundle,
  } = props;
  const originalPlanType =
    plan?.type?.toLowerCase() || plan?.planType?.toLowerCase() || '';
  const isDealerBundlePlan =
    isBundle &&
    (plan?.dealerPriceChecked ||
      plan?.plan?.toLowerCase() === PLAN_TYPE.DEALER ||
      !originalPlanType);

  const effectivePlanType =
    isBundle &&
    (originalPlanType === PLAN_TYPE.SUBSCRIPTION.toLowerCase() ||
      isDealerBundlePlan)
      ? PLAN_TYPE.BUNDLE_SUBSCRIPTION
      : originalPlanType;

  const planCTA = getPlanCTA(isAPIinCart, isSelected);
  const [pricingPlanToBeAdded, setPricingPlanToBeAdded] = useState('');

  useEffect(() => {
    if (!isLoading) setPricingPlanToBeAdded('');
  }, [isLoading]);

  return (
    <div
      className={`daab-plan-tile ${isSelected ? 'daab-selected-plan' : ''} ${
        isRetired ? 'daab-retired-plan' : ''
      }`}
    >
      {effectivePlanType === PLAN_TYPE.ADVANCED.toLowerCase() ? (
        <PlanDetails
          details={{
            type: plan.plan === PLAN_TYPE.CUSTOM ? plan.name : plan.plan,
            price: plan.price,
            description: plan.description,
            name: plan.name,
          }}
          isSelected={isSelected}
          isRetired={isRetired}
          planType={effectivePlanType}
          isBundle={isBundle}
        />
      ) : (
        <PlanDetails
          details={{
            type: effectivePlanType,
            price: plan?.subscriptionPrice || plan?.price,
            description:
              PLAN_CARD_TEXT[effectivePlanType]?.subtext ||
              plan?.description ||
              '',
            installFee: plan?.installFee,
            name: plan?.name,
          }}
          isSelected={isSelected}
          isRetired={isRetired}
          planType={effectivePlanType}
          isBundle={isBundle}
        />
      )}
      {effectivePlanType === PLAN_TYPE.ADVANCED.toLowerCase() ? (
        <EndpointContainers
          plan={plan}
          isAsync={isAsync}
          allEndpoints={allEndpoints}
          instanceIdVersionMap={instanceIdVersionMap}
          isSelected={isSelected}
          isRetired={isRetired}
        />
      ) : (
        <LegacyPlanContainers
          type={effectivePlanType}
          tiers={plan.rates || plan.tiers}
          freemiumUnit={plan.freemiumUnit}
          isSelected={isSelected}
          isRetired={isRetired}
          isBundle={isBundle}
        />
      )}
      {isRetired && (
        <div className="daab-plan-btn-container">
          <StatusIndicator
            label={statusIndicatorLabel}
            variant={STATUS_INDICATOR_VARIANTS.WARNING}
            className={'daab-retired-plan-status'}
          />
        </div>
      )}
      {!isAPIContracted && !isAPIPurchased && !isRetired && (
        <div className="daab-plan-btn-container">
          <Button
            dataTestId="button"
            onClick={() => {
              setPricingPlanToBeAdded(plan?.id);
              selectPricingPlan(plan?.id);
            }}
            isLoading={plan?.id === pricingPlanToBeAdded && isLoading}
            size="standard"
            text={planCTA}
            variant="primary"
            className={
              isSelected ? 'daab-selected-plan-btn' : 'daab-plan-select-btn'
            }
            isDisabled={isSelected}
          />
        </div>
      )}
    </div>
  );
};

const PlanDetails = ({
  details,
  isSelected,
  isRetired,
  planType,
  isBundle,
}) => {
  const { type, price, description, installFee, name } = details;
  const currentPlanType = (planType || type)?.toLowerCase();
  const isBundleSubscription =
    currentPlanType === PLAN_TYPE.BUNDLE_SUBSCRIPTION.toLowerCase();
  const isRegularSubscription =
    currentPlanType === PLAN_TYPE.SUBSCRIPTION.toLowerCase();

  const planTitle = getPlanName(
    isBundleSubscription ? PLAN_TYPE.SUBSCRIPTION : type,
    isBundle,
    name,
  );
  const resolvedPlanTitle =
    planTitle || name || PLAN_CARD_TEXT[currentPlanType]?.heading || '';

  return (
    <div className="daab-plan-details">
      <div
        className={`daab-plan-name ${isSelected ? 'daab-selected-plan' : ''} ${
          isRetired ? 'daab-retired-plan-name' : ''
        }`}
      >
        {resolvedPlanTitle}
      </div>
      <div className="daab-plan-description">{description}</div>
      {isBundleSubscription || isRegularSubscription ? (
        <>
          <div className="daab-subscription-pricing-container">
            <div
              className={`daab-plan-amount-subscription-plan ${
                isSelected ? 'daab-selected-plan' : ''
              } ${isRetired ? 'daab-retired-plan-amount' : ''}`}
              style={{
                fontSize: `${getPriceFontSize(price || 0)}px`,
                whiteSpace: 'nowrap'
              }}
            >
              {price ? formatCurrency(price) : '$0'}
              <span
                className={`daab-plan-location-text ${
                  isSelected ? 'daab-selected-plan' : ''
                } ${isRetired ? 'daab-retired-plan-interval' : ''}`}
                style={{
                  fontSize: `${getLocationFontSize(price || 0)}px`,
                  whiteSpace: 'nowrap'
                }}
              >
                /Location
              </span>
            </div>
            <div
              className={`daab-plan-per-month-text ${
                isSelected ? 'daab-selected-plan' : ''
              } ${isRetired ? 'daab-retired-plan-interval' : ''}`}
            >
              per month
            </div>
          </div>
          {installFee != null &&
            isBundle &&
            type === PLAN_TYPE.BUNDLE_SUBSCRIPTION && (
              <div className="daab-pricing-plan-tile-install-fee">
                <span aria-hidden="true">+</span> {formatCurrency(installFee)}{' '}
                Install Fee
              </div>
            )}
        </>
      ) : (
        <div
          className={`daab-plan-amount ${
            isSelected ? 'daab-selected-plan' : ''
          } ${isRetired ? 'daab-retired-plan-amount' : ''}`}
        >
          ${price ? formatNumbers(price) : 0}
          <span
            className={`daab-plan-interval ${
              isSelected ? 'daab-selected-plan' : ''
            } ${isRetired ? 'daab-retired-plan-interval' : ''}`}
          >
            {' '}
            / month
          </span>
        </div>
      )}
    </div>
  );
};

const EndpointContainers = ({
  plan,
  isAsync,
  allEndpoints,
  instanceIdVersionMap,
  isSelected,
  isRetired,
}) => {
  const [expandedContainer, setExpandedContainer] = useState(false);
  const state = useSelector((state) => state);
  const { fetchingAPIDetails } = state?.apiDirectorySlice;

  useEffect(() => {
    if (fetchingAPIDetails) {
      allEndpoints = [];
    }
  });
  const onExpanded = (index) => {
    setExpandedContainer(expandedContainer === index ? null : index);
  };

  return (
    <div className="daab-endpoint-containers">
      {Array.isArray(plan.containers) &&
        plan.containers?.map((container, index) => (
          <Container
            pricingDetails={{
              title: container.name,
              quota: container.quota,
              overage: container.overage,
              endpoints: container.endPoints,
              channels: container.channels,
              isFree: container?.isFree,
            }}
            index={index}
            key={index}
            isExpanded={index === expandedContainer}
            onExpanded={(index) => onExpanded(index)}
            allEndpoints={
              fetchingAPIDetails
                ? [Constants.LOADING]
                : allEndpoints?.length
                  ? allEndpoints
                  : [Constants.NO_DATA_FOUND]
            }
            isAsync={isAsync}
            instanceIdVersionMap={instanceIdVersionMap}
            isSelected={isSelected}
            isRetired={isRetired}
          />
        ))}
    </div>
  );
};

const Container = ({
  pricingDetails: { title, quota, overage, endpoints, channels, isFree },
  index,
  onExpanded,
  isExpanded,
  isAsync,
  allEndpoints = [],
  instanceIdVersionMap,
  isSelected,
  isRetired,
}) => {
  const paths = isAsync ? channels : endpoints;
  const containerEndpoints =
    paths && paths.length
      ? constructEndpointTitle(paths, instanceIdVersionMap)
      : allEndpoints;
  const callsPerMonthText = `${
    isFree ? 'Unlimited' : formatNumbers(quota)
  } free calls per month`;

  const overageText = `$${overage} overage fee`;

  return (
    <div className="daab-endpoint-container">
      <div className="daab-container-title">
        <IconCheckCircle
          className={`${
            isRetired ? 'daab-retired-check-icon' : 'daab-check-icon'
          } ${isSelected ? 'daab-selected-plan' : ''}`}
        />
        <div className="daab-container-title-text">{title}</div>
      </div>
      <div className="daab-adv-pp-container-details">
        {isExpanded ? (
          <IconArrowDropDown
            onClick={() => onExpanded(index)}
            data-testid="daab-collapse-icon"
            className={`daab-arrow-icon ${
              isSelected ? 'daab-selected-plan' : ''
            }`}
          />
        ) : (
          <IconArrowCascadeRight
            onClick={() => onExpanded(index)}
            data-testid="daab-expand-icon"
            className={`daab-arrow-icon ${
              isSelected ? 'daab-selected-plan' : ''
            }`}
          />
        )}

        <div className="daab-adv-plan-container">
          <div>{callsPerMonthText}</div>

          <div>{overageText}</div>
        </div>
      </div>

      {isExpanded && (
        <div
          className={`daab-adv-plan-endpoint-list ${
            isSelected ? 'daab-selected-plan' : ''
          }`}
          data-testid="daab-endpoint-list"
        >
          {containerEndpoints.map((endpoint, index) => (
            <div
              key={index}
              className={`daab-endpoint-name ${
                isSelected ? 'daab-selected-plan' : ''
              }`}
            >
              {endpoint}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
const constructEndpointTitle = (endpoints, instanceIdVersionMap) => {
  const instanceMapArray = Array.isArray(instanceIdVersionMap)
    ? instanceIdVersionMap
    : [];

  const title = Array.isArray(endpoints)
    ? endpoints.map((ep) => {
        const desc = ep.summary || ep.route?.replace('/', '');
        const version =
          instanceMapArray.find((f) => f.apiInstanceId === ep.apiInstanceId)
            ?.version || '';
        return `${ep?.verb?.padEnd(9, ' ')} ${version}-${desc}`;
      })
    : [];

  return title;
};

const LegacyPlanContainers = ({
  type,
  tiers,
  price,
  freemiumUnit,
  isSelected,
  isRetired,
}) => {
  const title = 'All Endpoints';
  const normalizedType = type?.toLowerCase();
  const isBundleSubscription =
    normalizedType === PLAN_TYPE.BUNDLE_SUBSCRIPTION.toLowerCase();
  const isRegularSubscription =
    normalizedType === PLAN_TYPE.SUBSCRIPTION.toLowerCase();

  const callsPerMonthText = isBundleSubscription
    ? '0 free calls per month per location'
    : isRegularSubscription
      ? 'Unlimited free calls per month.'
      : '';

  const overageText =
    (isBundleSubscription || isRegularSubscription) && 'No overage fees.';

  return (
    <div className="daab-endpoint-containers">
      <div className="daab-endpoint-container">
        {isBundleSubscription && (
          <>
            <div className="daab-container-title-subscription-plan">
              <IconCheckCircle
                className={`${
                  isRetired ? 'daab-retired-check-icon' : 'daab-check-icon'
                } ${isSelected ? 'daab-selected-plan' : ''}`}
              />
              <div className="daab-container-title-text-subscription-plan">
                {title}
              </div>
            </div>
            <div className="daab-plan-container-quota-overage-subscription-plan">
              <div className="daab-plan-container-quota-subscription-plan">
                {callsPerMonthText}
              </div>
              <div className="daab-plan-container-overage-subscription-plan">
                {overageText}
              </div>
            </div>
          </>
        )}
        {isRegularSubscription && (
          <>
            <div className="daab-check-icon-plan-frequency">
              <IconCheckCircle
                className={`${
                  isRetired ? 'daab-retired-check-icon' : 'daab-check-icon'
                } ${isSelected ? 'daab-selected-plan' : ''}`}
              />
              <div className="daab-plan-frquency-text">Monthly billing</div>
            </div>
            <div className="daab-check-icon-plan-frequency">
              <IconCheckCircle
                className={`${
                  isRetired ? 'daab-retired-check-icon' : 'daab-check-icon'
                } ${isSelected ? 'daab-selected-plan' : ''}`}
              />
              <div className="daab-plan-limit-text">Unlimited usage</div>
            </div>
            <div className="daab-container-title-subscription-plan">
              <IconCheckCircle
                className={`${
                  isRetired ? 'daab-retired-check-icon' : 'daab-check-icon'
                } ${isSelected ? 'daab-selected-plan' : ''}`}
              />
              <div className="daab-container-title-text-subscription-plan">
                {title}
              </div>
            </div>
            <div className="daab-plan-container-quota-overage-subscription-plan">
              <div className="daab-plan-container-quota-subscription-plan">
                {callsPerMonthText}
              </div>
              <div className="daab-plan-container-overage-subscription-plan">
                {overageText}
              </div>
            </div>
          </>
        )}

        {!isBundleSubscription && !isRegularSubscription && (
          <>
            <div className="daab-container-title">
              <IconCheckCircle
                className={`${
                  isRetired ? 'daab-retired-check-icon' : 'daab-check-icon'
                } ${isSelected ? 'daab-selected-plan' : ''}`}
              />
              <div className="daab-container-title-text">{title}</div>
            </div>
            {normalizedType !== PLAN_TYPE.TRANSACTIONAL.toLowerCase() && (
              <div className="daab-sub-plan-container">
                <div>{callsPerMonthText}</div>
                <div>{overageText}</div>
              </div>
            )}
          </>
        )}
        <div>
          {normalizedType === PLAN_TYPE.TRANSACTIONAL.toLowerCase() &&
            Array.isArray(tiers) &&
            tiers?.map((tier, index) => {
              const rate = tier.pricePerRequest
                ? tier.pricePerRequest
                : tier.rate;
              const callsText = `Call ${formatNumbers(tier.startUnit)} - ${
                (tier.endUnit && formatNumbers(tier.endUnit)) || 'Unlimited'
              }`;
              const ratesText = `$${formatNumbers(rate)}/call`;
              return (
                <div className="daab-plan-tiers" key={index}>
                  <div className="daab-plan-tiers-calls">{callsText}</div>
                  <div className="daab-plan-tiers-rates">{ratesText}</div>
                </div>
              );
            })}
        </div>
      </div>
    </div>
  );
};

export { APIPricingPlanTile };
